<script setup lang="ts">
import { useAdminGetLinkItem, adminGetMetabaseIframeUrl } from '@swipe4work/api-orval'
import { LinkItemType, MetabaseResourceType } from '@swipe4work/models-orval'
import { useTopBarTitle } from '@/shared/composables/UseTopBarTitle'

definePage({
  path: '/dashboard/view/:id',
  name: '/dashboard/DashboardDetail',
  meta: {
    title: 'Dashboard',
    breadcrumbParent: '/homepage/Homepage',
  },
})

const route = useRoute('/dashboard/DashboardDetail')
const id = computed(() => Number((route.params as { id: string }).id))

const { data: item, isPending } = useAdminGetLinkItem(id, { query: { staleTime: 30 * 1000 } })

// The topbar shows the link item's name as the last breadcrumb once loaded.
useTopBarTitle(() => item.value?.name)

const metabaseIframeUrl = ref<string | null>(null)

watch(item, async (value) => {
  metabaseIframeUrl.value = null

  if (!value || value.type !== LinkItemType.MetabaseIframe || !value.resourceId) {
    return
  }

  /*
   * Sign the embed URL via the main API. initialParameters are appended as embed query params
   * (not locked into the token), matching the previous behaviour.
   */
  const { url: signedUrl } = await adminGetMetabaseIframeUrl({
    resourceType: value.resourceType ?? MetabaseResourceType.Dashboard,
    resourceId: value.resourceId,
  })

  const url = new URL(signedUrl)

  url.hash = '#background=false&bordered=false&locale=nl&theme=transparent'

  for (const [ key, val ] of Object.entries(value.initialParameters ?? {})) {
    url.searchParams.set(key, String(val))
  }

  metabaseIframeUrl.value = url.toString()
}, { immediate: true })
</script>

<template>
  <div class="py-2 space-y-5">
    <div v-if="isPending" class="space-y-3">
      <Skeleton height="2rem" width="20rem" />
      <Skeleton height="40rem" />
    </div>
    
    <template v-else-if="item">
      <!-- Metabase dashboard / question -->
      <CardWithHeader
        v-if="item.type === LinkItemType.MetabaseIframe"
        :title="item.name"
        :options="{ removeBodyPadding: true }"
      >
        <div v-if="!metabaseIframeUrl" class="p-4 space-y-3">
          <Skeleton height="2rem" />
          <Skeleton height="60rem" />
        </div>
        <iframe
          v-else
          :src="metabaseIframeUrl"
          width="100%"
          height="1200px"
          class="border-0"
        />
      </CardWithHeader>

      <!-- Plain iframe -->
      <CardWithHeader
        v-else-if="item.type === LinkItemType.Iframe"
        :title="item.name"
        :options="{ removeBodyPadding: true }"
      >
        <iframe
          :src="item.iframeUrl ?? ''"
          width="100%"
          :height="`${item.iframeHeight ?? 1200}px`"
          class="border-0"
        />
      </CardWithHeader>

      <!-- External uri -->
      <Card v-else>
        <template #content>
          <div class="flex flex-col items-start gap-3">
            <p class="m-0 text-surface-600">
              Deze link verwijst naar een externe pagina.
            </p>
            <a :href="item.uri ?? '#'" target="_blank" rel="noopener">
              <Button label="Open externe link" icon="fa-regular fa-arrow-up-right-from-square" />
            </a>
          </div>
        </template>
      </Card>
    </template>

    <Message v-else severity="error">
      Item niet gevonden.
    </Message>
  </div>
</template>
